import { useEffect } from "react";

/**
 * THE CLICK THAT CLOSES A TRANSIENT SURFACE DOES NOTHING ELSE.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 * WHY THIS LIVES IN `hooks/` AND NOT UNDER `crm/table/edit/`
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * It was written for the CRM cell editor and was named for it, but nothing in it knows
 * what a cell is: it takes a containment test and a callback. The quick-notes popover —
 * a different surface, on a different page, reported separately — needs exactly the same
 * guarantee, so the file moved rather than being copied. A second copy of a listener that
 * attaches to `document` in the capture phase is the kind of duplication that ends with two
 * of them disagreeing about which one swallowed the click.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 * This REVERSES a documented decision, and the reversal was asked for
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * The non-modal editor surface (`Popper` + `ClickAwayListener`) deliberately had no
 * backdrop, and `CrmCellControls`' header still explains the intent: *"no backdrop, so the
 * click that dismisses this editor ALSO lands on the next cell and opens it. One click to
 * move on, which is the fill-then-move-on flow."*
 *
 * In use it is not experienced as moving on — it is experienced as an editor you cannot
 * put away: *"when editing a field in the table, when clicking outside, I would like to
 * capture the click and just close the editing. right now we then open edit on another
 * field — not great."* The reason the two readings differ is that the pointer is very
 * rarely aimed at the NEXT field to fill. It is aimed at a value being read, at a row being
 * opened, at the filter bar — and each of those turned into an editor the realtor then had
 * to dismiss, which is one more click than the "saving" it was meant to spare them.
 *
 * The quick-notes popover was rejected for the same thing in the same words, on the mobile
 * card: *"on click outside of the quick-notes input we want to capture that click, and not
 * trigger what's inside"* — there, the click that put the notes away was also opening the
 * contact underneath it.
 *
 * It also settles an inconsistency that predates this file. Tags, cities and neighborhoods
 * already render on a `Popover`, whose Modal backdrop has ALWAYS swallowed the dismissing
 * click. So the product had two dismissal behaviours depending on which column you happened
 * to be editing, with nothing on screen to distinguish them. Now there is one.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 * WHY THIS IS TWO LISTENERS AND NOT A `ClickAwayListener` OPTION
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * By the time the `click` event exists, the editor is already gone — so there is nothing
 * left to hang a "swallow this" on:
 *
 *   pointerdown  → focus leaves the editor's input
 *                → `onBlur` on the Paper fires and COMMITS
 *                → the host unmounts the editor, and every listener it owned with it
 *   click        → lands on whatever is now under the pointer, and opens ITS editor
 *
 * The swallow therefore has to be armed on `pointerdown` and has to OUTLIVE the component
 * that armed it. That is why the click listener below is attached to `document` directly
 * and removes ITSELF, rather than being cleaned up by the effect: the effect's cleanup runs
 * during the very unmount this is designed to survive.
 *
 *  - **`pointerdown`, not `mousedown`** — one event covers mouse, touch and pen. On a
 *    tablet the whole problem repeats with a finger.
 *  - **CAPTURE phase on `document`** — React 17 attaches its listeners to the app's root
 *    container, which is a descendant of `document`. Stopping propagation here means the
 *    synthetic event is never created at all, so no React `onClick` anywhere in the tree
 *    can see it. A bubble-phase listener would run last, after every handler it was meant
 *    to pre-empt.
 *  - **`preventDefault` as well as `stopPropagation`** — a row can contain a real `<a
 *    href="tel:…">`, and a navigation is a default action rather than a listener.
 *
 * ⚠️ THE POINTERDOWN HANDLER MUST NOT `preventDefault`. That would suppress the focus
 * change, and with it the blur — which is the path that carries a half-typed value into the
 * commit. Dismissing must never mean discarding: this closes the editor by COMMITTING it,
 * exactly as clicking away always did.
 *
 * ── The TTL, and the one gesture it covers ──────────────────────────────────────
 *
 * A `pointerdown` does not always produce a `click`: a press that turns into a drag, a
 * scroll, a context menu, a pointer that leaves the window. Without a deadline the armed
 * swallow would sit on `document` and eat some entirely unrelated click minutes later. 800
 * ms is comfortably longer than a deliberate slow click and far shorter than the gap to the
 * next intentional one.
 */

/** How long an armed swallow waits for the click that pairs with its pointerdown. */
const SWALLOW_TTL_MS = 800;

/**
 * HAND THE FOCUSED CONTROL ITS BLUR BEFORE ANYTHING COMMITS.
 *
 * ⚠️ WITHOUT THIS, A CONTROL THAT LIFTS ITS VALUE ON BLUR LOSES IT. Reported against the
 * rooms picker: `ScaleStepper` keeps the number being typed in its OWN state and calls
 * `onChange` from `onBlur`, so a dismissal that runs on `pointerdown` — before the browser
 * has moved focus — commits a draft that was never told about the typing. The realtor's
 * own workaround was to click something else inside the picker first, which is precisely a
 * manual blur.
 *
 * Running it here rather than relying on the focus change that follows is deliberate: this
 * handler is on `pointerdown`, which fires BEFORE the default action moves focus, and by
 * the time that happens the editor is unmounted and its blur handler is gone with it.
 *
 * `.blur()` dispatches synchronously, so React 17 runs the control's handler and flushes
 * the state it sets before this function returns — which is what makes the ordering above
 * safe. It is also why `CrmCellEditor` writes its draft to a ref inside the setter rather
 * than reading it back off the render: belt and braces for the same class of bug.
 */
const blurFocusedControl = (isInside: (target: Node) => boolean): void => {
  const focused = document.activeElement;
  if (!focused || focused === document.body) return;
  if (!(focused instanceof HTMLElement) || !isInside(focused)) return;
  focused.blur();
};

export function useOutsideDismiss({
  enabled,
  isInside,
  onDismiss,
  shouldSwallow
}: {
  /**
   * Only while the surface is mounted AND past the arming tick. The click that OPENED the
   * editor is still propagating when React commits the mount, and a listener added
   * mid-propagation still receives it — so an unarmed version reads the opening click as a
   * click away and closes the editor in the same gesture that asked for it.
   */
  enabled: boolean;
  /** Is this node part of the editor? The surface is portalled, so test the SURFACE, not the cell. */
  isInside: (target: Node) => boolean;
  /** Commit and close. Must be idempotent — the blur path can reach it for the same gesture. */
  onDismiss: () => void;
  /**
   * MAY THIS PARTICULAR OUTSIDE TAP KEEP ITS CLICK? Default: no, swallow it — that is the
   * whole point of the hook.
   *
   * The exception the quick-notes popover needs: a tap on ANOTHER quick-notes trigger. Every
   * row renders its own popover, so row B's trigger is "outside" row A's — and swallowing
   * there means A closes, B's `onClick` never fires, and moving between two rows' notes costs
   * TWO taps where the `ClickAwayListener` this replaced cost one. That regression would have
   * ridden out to all five surfaces that render the popover, none of which asked for it.
   *
   * Returning false still DISMISSES; it only declines to eat the click, so the other trigger
   * opens in the same gesture. Keep such predicates narrow: anything that answers false is
   * something the dismissing tap is allowed to operate, which is exactly what the default
   * exists to prevent.
   */
  shouldSwallow?: (target: Node) => boolean;
}): void {
  useEffect(() => {
    if (!enabled) return undefined;

    const armClickSwallow = (): void => {
      let timer = 0;
      const swallow = (event: MouseEvent): void => {
        event.stopPropagation();
        event.preventDefault();
        disarm();
      };
      const disarm = (): void => {
        document.removeEventListener("click", swallow, true);
        window.clearTimeout(timer);
      };
      timer = window.setTimeout(disarm, SWALLOW_TTL_MS);
      document.addEventListener("click", swallow, true);
    };

    const onPointerDown = (event: PointerEvent): void => {
      const target = event.target as Node | null;
      if (!target || isInside(target)) return;
      // Armed BEFORE dismissing: `onDismiss` unmounts this component synchronously, and
      // anything scheduled after it would be scheduled from a dead closure.
      if (shouldSwallow?.(target) !== false) armClickSwallow();
      blurFocusedControl(isInside);
      onDismiss();
    };

    document.addEventListener("pointerdown", onPointerDown, true);
    return () => document.removeEventListener("pointerdown", onPointerDown, true);
  }, [enabled, isInside, onDismiss, shouldSwallow]);
}
